import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOpenParam } from '../hooks/useOpenParam';
import apiClient from '../api/client';
import { DataGrid, type Column } from '../components/ui/DataGrid';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { formatEUR, formatDate } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import { Download, X } from 'lucide-react';

interface Invoice {
  id: string;
  invoice_number: string;
  customer_id: string | null;
  order_id: string | null;
  status: string;
  subtotal_ht: string;
  vat_amount: string;
  total_ttc: string;
  issued_at: string;
}

/** Screens 33-34 — Invoices list (ACC, OWN, B2B_*). B2B sees own company only. */
export default function Invoices() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  // FEC export and the cross-company customer link are accounting/staff features.
  // B2B users only carry INVOICE_VIEW_OWN_COMPANY, so these stay hidden for them.
  const canExportFec = usePermission(Permission.ACCOUNTING_EXPORT);
  const canViewAllInvoices = usePermission(Permission.INVOICE_VIEW_ALL);
  const [rows, setRows] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [params, setParams] = useSearchParams();
  // Back from CAWL (?invoice=<id>&paid=1 | &canceled=1). Coming back is not proof of payment: the
  // server asks CAWL and the toast says what CAWL answered. The list loads after that answer, so
  // the invoice shows paid when it is.
  const [returnSettled, setReturnSettled] = useState(false);
  useEffect(() => {
    const invoiceId = params.get('invoice');
    const paid = params.get('paid') === '1';
    const canceled = params.get('canceled') === '1';
    if (!invoiceId || (!paid && !canceled)) {
      setReturnSettled(true);
      return;
    }
    const next = new URLSearchParams(params);
    ['invoice', 'paid', 'canceled'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    void (async () => {
      let status: string | null = null;
      if (paid) {
        try {
          const { data } = await apiClient.post<{ status: string }>(
            `/invoices/${invoiceId}/payment/sync`,
            {}
          );
          status = data.status;
        } catch {
          // Not a payment failure: CAWL's notification still settles it.
        }
      }
      const key = canceled
        ? 'canceled'
        : status === 'SUCCEEDED'
          ? 'paid'
          : status === 'FAILED'
            ? 'failed'
            : 'pending';
      push({
        tone: key === 'paid' ? 'success' : key === 'failed' ? 'error' : 'warning',
        title: t(`invoices.cardPay.${key}Title`),
        message: t(`invoices.cardPay.${key}Message`),
      });
      setReturnSettled(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!returnSettled) return;
    apiClient
      .get<Invoice[]>('/invoices')
      .then((r) => setRows(r.data ?? []))
      .catch(showApiError)
      .finally(() => setLoading(false));
  }, [showApiError, returnSettled]);
  // Arriving from the Documents screen with `?open=<id>`: open that invoice's panel.
  useOpenParam(rows, loading, setSelected);

  // The FEC endpoint is auth-gated; a plain <a href> can't carry the Bearer token,
  // so fetch it as an authenticated blob and trigger the download client-side.
  const exportFec = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/invoices/exports/fec', { responseType: 'blob' });
      const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
      const filename = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? 'fec.txt';
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<Invoice>[] = [
    { key: 'invoice_number', header: t('invoices.columns.number') },
    {
      key: 'status',
      header: t('common.status'),
      render: (r) => enumLabel('invoiceStatus', r.status),
    },
    {
      key: 'total_ttc',
      header: t('invoices.columns.totalTtc'),
      render: (r) => `${formatEUR(Number(r.total_ttc))}`,
    },
    { key: 'issued_at', header: t('invoices.columns.issued') },
  ];

  return (
    <div>
      <PageHeader
        title={t('invoices.title')}
        description={
          canViewAllInvoices ? t('invoices.descriptionAll') : t('invoices.descriptionOwn')
        }
        actions={
          canExportFec ? (
            <Button variant="outline" size="sm" disabled={exporting} onClick={exportFec}>
              <Download className="h-4 w-4" />{' '}
              {exporting ? t('invoices.exporting') : t('invoices.exportFec')}
            </Button>
          ) : undefined
        }
      />
      <DataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
      />
      {selected && (
        <InvoiceDetailPanel
          invoice={selected}
          showCustomerLink={canViewAllInvoices}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/** Right-side slide-in showing invoice summary + an inline PDF preview, so a row
 *  click previews the invoice in place (the list row carries all summary fields;
 *  the full document comes from the authenticated /invoices/:id/pdf endpoint). */
function InvoiceDetailPanel({
  invoice,
  showCustomerLink,
  onClose,
}: {
  invoice: Invoice;
  showCustomerLink: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  /* Card payment on an open invoice (2026-10-21) — the B2B owner settles here, e.g. to lift a
     credit-limit or collection block. The server says whether it is payable and for how much
     (net of avoirs and what is already paid); the button only appears when it is. */
  const canPayOnline = usePermission(Permission.ORDER_PAY_ONLINE);
  const [cardOffer, setCardOffer] = useState<{ offered: boolean; amount: number } | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  useEffect(() => {
    if (!canPayOnline) return;
    apiClient
      .get<{ offered: boolean; amount: number }>(`/invoices/${invoice.id}/checkout`)
      .then((r) => setCardOffer(r.data))
      .catch(() => setCardOffer(null));
  }, [invoice.id, canPayOnline]);
  const payByCard = async () => {
    setCardBusy(true);
    try {
      const { data } = await apiClient.post<{ checkoutUrl: string }>(
        `/invoices/${invoice.id}/checkout`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      window.location.href = data.checkoutUrl;
    } catch (err) {
      showApiError(err);
      setCardBusy(false);
    }
  };

  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
        if (!alive) return;
        url = URL.createObjectURL(res.data as Blob);
        setPdfUrl(url);
      } catch (err) {
        showApiError(err);
      }
    })();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [invoice.id, showApiError]);

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex justify-between gap-4 py-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="truncate text-lg font-semibold" title={invoice.invoice_number}>
            {t('invoices.heading', { number: invoice.invoice_number })}
          </h2>
          <div className="flex items-center gap-2">
            {cardOffer?.offered && (
              <Button size="sm" disabled={cardBusy} onClick={() => void payByCard()}>
                {cardBusy
                  ? t('invoices.cardPay.redirecting')
                  : t('invoices.cardPay.action', { amount: formatEUR(cardOffer.amount) })}
              </Button>
            )}
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={`facture-${invoice.invoice_number}.pdf`}
                className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <Download className="h-4 w-4" /> PDF
              </a>
            )}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label={t('invoices.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <section className="rounded border bg-card p-4">
            <dl className="text-sm">
              <Row label={t('invoices.rows.number')}>
                <span className="font-mono">{invoice.invoice_number}</span>
              </Row>
              <Row label={t('invoices.rows.status')}>
                {enumLabel('invoiceStatus', invoice.status)}
              </Row>
              <Row label={t('invoices.rows.issued')}>{formatDate(invoice.issued_at)}</Row>
              <Row label={t('invoices.rows.customer')}>
                {invoice.customer_id ? (
                  showCustomerLink ? (
                    <Link
                      to={`/customers/${invoice.customer_id}`}
                      className="font-mono text-primary hover:underline"
                    >
                      {invoice.customer_id}
                    </Link>
                  ) : (
                    <span className="font-mono">{invoice.customer_id}</span>
                  )
                ) : (
                  '—'
                )}
              </Row>
              <Row label={t('invoices.rows.order')}>
                {invoice.order_id ? (
                  <Link
                    to={`/orders/${invoice.order_id}`}
                    className="font-mono text-primary hover:underline"
                  >
                    {invoice.order_id}
                  </Link>
                ) : (
                  '—'
                )}
              </Row>
              <Row label={t('invoices.rows.subtotalHt')}>{formatEUR(invoice.subtotal_ht)}</Row>
              <Row label={t('invoices.rows.vat')}>{formatEUR(invoice.vat_amount)}</Row>
              <Row label={t('invoices.rows.totalTtc')}>
                <span className="font-semibold">{formatEUR(invoice.total_ttc)}</span>
              </Row>
            </dl>
          </section>

          <section className="flex min-h-[24rem] flex-1 flex-col rounded border bg-card">
            <h3 className="border-b px-3 py-2 text-sm font-semibold">{t('invoices.document')}</h3>
            {pdfUrl ? (
              <iframe
                title={t('invoices.heading', { number: invoice.invoice_number })}
                src={pdfUrl}
                className="h-full w-full flex-1 rounded-b"
              />
            ) : (
              <div className="p-4">
                <Skeleton className="h-80 w-full" />
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
